import { Bottle } from '../types/bottle'
import { memo } from 'react'

interface BottleCardProps {
  bottle: Bottle
  onReanalyze?: (bottleId: string) => void
  onDelete?: (bottleId: string) => void
  onBottleClick?: (bottleId: string) => void
}

// 解析中など未採番（0）のボトルは番号を表示しない（BM-005）
const formatBottleNumber = (value: number | string | null | undefined) => {
  const n = Number(value)
  return Number.isInteger(n) && n > 0 ? String(n) : '未採番'
}

// 解析中のまま一定時間（10分）経過したボトルは再解析できる（BM-007。サーバー側と同じ閾値）
const STALE_PENDING_MS = 10 * 60 * 1000
const isStalePending = (startedAt?: string) =>
  !startedAt || Date.now() - new Date(startedAt).getTime() >= STALE_PENDING_MS

export const BottleCard = memo(function BottleCard({ bottle, onReanalyze, onDelete, onBottleClick }: BottleCardProps) {
  const handleClick = () => {
    onBottleClick?.(bottle.bottle_id);
  };
  // 解析中
  if (bottle.analysis_status === 'pending') {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="animate-spin h-4 w-4 border-2 border-yellow-600 border-t-transparent rounded-full"></div>
          <span className="font-semibold text-yellow-800">画像解析中...</span>
        </div>
        <p className="text-sm text-gray-600">ボトル番号: {formatBottleNumber(bottle.bottle_number)}</p>
        {bottle.analysis_started_at && (
          <p className="text-xs text-gray-500 mt-1">
            開始: {new Date(bottle.analysis_started_at).toLocaleString('ja-JP')}
          </p>
        )}
        {onReanalyze && isStalePending(bottle.analysis_started_at) && (
          <div className="mt-3 flex justify-center">
            <button
              type="button"
              onClick={() => onReanalyze(bottle.bottle_id)}
              className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 text-sm"
            >
              解析が終わらない場合は再解析
            </button>
          </div>
        )}
      </div>
    )
  }

  // 解析失敗
  if (bottle.analysis_status === 'failed') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-3">
        <p className="font-semibold text-red-800 mb-2">解析に失敗しました</p>
        <p className="text-sm text-gray-600 mb-2">ボトル番号: {formatBottleNumber(bottle.bottle_number)}</p>
        {bottle.error_message && (
          <p className="text-sm text-red-600 mb-3">{bottle.error_message}</p>
        )}
        <div className="flex justify-center gap-2">
          {onReanalyze && (
            <button
              onClick={() => onReanalyze(bottle.bottle_id)}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm"
            >
              再解析
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(bottle.bottle_id)}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 text-sm border border-red-300"
            >
              削除
            </button>
          )}
        </div>
      </div>
    )
  }

  // 属性未更新
  if (!bottle.attributes_updated && bottle.analysis_status === 'completed') {
    return (
      <div
        className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3 cursor-pointer hover:bg-blue-100"
        onClick={handleClick}
      >
        <div className="flex justify-between items-start mb-2">
          <p className="font-semibold text-gray-800">{bottle.product_name}</p>
          <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded">属性未更新</span>
        </div>
        <p className="text-sm text-gray-600">ボトル番号: {formatBottleNumber(bottle.bottle_number)}</p>
        <p className="text-sm text-gray-600">種類: {bottle.bottle_type}</p>
        {bottle.producer && <p className="text-sm text-gray-600">生産者: {bottle.producer}</p>}
      </div>
    )
  }

  // 属性確認待ち（未公開）
  if (bottle.is_published === false) {
    return (
      <div
        className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-3 cursor-pointer hover:bg-yellow-100"
        onClick={handleClick}
      >
        <div className="flex justify-between items-start mb-2">
          <p className="font-semibold text-gray-800">{bottle.product_name}</p>
          <span className="text-xs bg-orange-500 text-white px-2 py-1 rounded">属性確認待ち</span>
        </div>
        <p className="text-sm text-gray-600">ボトル番号: {formatBottleNumber(bottle.bottle_number)}</p>
        <p className="text-sm text-gray-600">種類: {bottle.bottle_type}</p>
        {bottle.producer && <p className="text-sm text-gray-600">生産者: {bottle.producer}</p>}
        <p className="text-xs text-orange-600 mt-2">属性を確認して公開してください</p>
      </div>
    )
  }

  // 開栓ステータス判定
  const getOpeningStatus = () => {
    if (bottle.opened_date) {
      return { label: '開栓済み', bgColor: 'bg-blue-100', textColor: 'text-blue-800' }
    }
    return { label: '未開栓', bgColor: 'bg-green-100', textColor: 'text-green-800' }
  }

  const openingStatus = getOpeningStatus()

  // 残量ステータス変換
  const getRemainingStatus = () => {
    if (bottle.remaining_status === 'remaining') return '残あり'
    if (bottle.remaining_status === 'empty') return '空'
    return '完売'
  }

  // 開栓日フォーマット
  const formatOpenedDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`
  }

  // 通常表示
  return (
    <div
      className="bg-white border border-gray-200 rounded-lg p-4 mb-3 cursor-pointer hover:bg-gray-50"
      onClick={handleClick}
    >
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-lg font-bold text-gray-900 flex-1">
          {bottle.product_name}{Number(bottle.bottle_number) > 0 && ` #${bottle.bottle_number}`}
        </h3>
        <div className="ml-2 flex flex-col items-end">
          <span className={`px-3 py-1 ${openingStatus.bgColor} ${openingStatus.textColor} text-sm rounded whitespace-nowrap`}>
            {openingStatus.label}
          </span>
          {bottle.opened_date && (
            <span className="text-xs text-gray-500 mt-1">
              {formatOpenedDate(bottle.opened_date)}
            </span>
          )}
        </div>
      </div>
      <div className="text-center text-sm text-gray-600 mb-2">
        {bottle.producer || '生産者不明'}
      </div>
      <div className="flex justify-center gap-4 text-sm text-gray-600">
        <span>産地: {bottle.country || '不明'}</span>
        <span className={bottle.remaining_status === 'empty' ? 'text-red-600' : ''}>
          残量: {getRemainingStatus()}
        </span>
      </div>
    </div>
  )
}, (prevProps, nextProps) => {
  // true を返すと再レンダリングをスキップ
  // false を返すと再レンダリング
  
  // bottle_id が異なる場合は再レンダリング
  if (prevProps.bottle.bottle_id !== nextProps.bottle.bottle_id) {
    return false;
  }
  
  // 重要フィールドを比較
  const bottleUnchanged = (
    prevProps.bottle.analysis_status === nextProps.bottle.analysis_status &&
    prevProps.bottle.updated_at === nextProps.bottle.updated_at &&
    prevProps.bottle.attributes_updated === nextProps.bottle.attributes_updated &&
    prevProps.bottle.is_published === nextProps.bottle.is_published &&
    prevProps.bottle.product_name === nextProps.bottle.product_name &&
    prevProps.bottle.opened_date === nextProps.bottle.opened_date &&
    prevProps.bottle.remaining_status === nextProps.bottle.remaining_status &&
    prevProps.bottle.error_message === nextProps.bottle.error_message
  );
  
  // コールバック関数の参照比較
  const callbacksUnchanged = (
    prevProps.onReanalyze === nextProps.onReanalyze &&
    prevProps.onDelete === nextProps.onDelete &&
    prevProps.onBottleClick === nextProps.onBottleClick
  );
  
  // 全て変更なしの場合のみ true（再レンダリングスキップ）
  return bottleUnchanged && callbacksUnchanged;
})
